import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaEuroSign, FaLock,FaBrain , FaMagic, FaExchangeAlt, FaSave , FaStream } from 'react-icons/fa';
import SeoHelmet from './SeoHelmet';
import MonoButton from '../kits/DesignKit/MonoButton';
import RoughBorder from '../kits/LayoutKit/RoughBorder';
import { PaperGradientBG } from '../kits';

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';

const ZenPostStudioLandingPageHero = () => {
  const navigate = useNavigate();

  // Refs für RoughBorder
  const problemCard1Ref = useRef();
  const problemCard2Ref = useRef();
  const problemCard3Ref = useRef();
  const featureCard1Ref = useRef();
  const featureCard2Ref = useRef();
  const featureCard3Ref = useRef();
  const featureCard4Ref = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const trackCTA = (ctaName) => {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'ZenPostStudio',
        event_label: ctaName,
      });
    }
  };

  const problems = [
    {
      icon: <FaClock className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Wiederholende Arbeit",
      description: "Gleicher Inhalt, unterschiedliche Formate. Copy-Paste für jede Plattform.",
      ref: problemCard1Ref
    },
    {
      icon: <FaExchangeAlt className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Laufende Kosten",
      description: "Cloud-KI-Dienste kosten monatlich. Für jeden im Team.",
      ref: problemCard2Ref
    },
    {
      icon: <FaLock className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Datenschutz",
      description: "Deine Inhalte gehen durch externe Server. Nicht ideal für sensible Texte.",
      ref: problemCard3Ref
    }
  ];

  const features = [
    {
      icon: <FaMagic className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Transformation",
      description: "Wandle jeden Artikel sofort in 9+ Plattform Posts um.",
      ref: featureCard1Ref
    },
    {
      icon: <FaBrain className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Plattform-Optimiert",
      description: "Automatische Anpassung an Ton, Länge & Format.",
      ref: featureCard2Ref
    },
    {
      icon: <FaSave className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Lokale Dokumente",
      description: "Relevante Ablage auf deinem Desktop für maximale Sicherheit.",
      ref: featureCard3Ref
    },
    {
      icon: <FaStream className="text-brand text-2xl animate-spin [animation-duration:6s]" />,
      title: "Data Room",
      description: "Erstelle echte Dokumente für deine Projekte.",
      ref: featureCard4Ref
    }
  ];

  return (
    <>
      <SeoHelmet
        title="ZenPost Studio – Schreiben und Publizieren mit lokaler KI"
        description="Schreibe einmal, erstelle Posts für 9 Plattformen: LinkedIn, dev.to, Medium, X, Reddit und mehr. Kostenlose Desktop-App mit lokaler KI."
        keywords="schreiben, publizieren, multi-plattform, lokale ki, ollama, content editor"
        ogImage="/images/zenpoststudio-og.jpg"
      />

      <PaperGradientBG variant="zenBlack">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 font-mono">

          {/* ========== HERO ========== */}
          <section className="text-center mb-16 sm:mb-24">
            {/* Eyebrow Badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-brand bg-transparent px-4 py-1.5 text-xs sm:text-sm  text-[#EFEBDC] mb-6">
              <span className="h-2 w-2 rounded-full bg-brand" />
              Multi-Plattform Publishing
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#EFEBDC] mb-4">
              Schreibe einmal.
              <span className="block text-brand mt-2">
                Publiziere überall.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base text-[#EFEBDC] max-w-3xl mx-auto leading-relaxed mb-8">
              Verwandle einen Artikel in Posts für 9+ Plattformen – LinkedIn, dev.to, Medium, X, Reddit, GitHub, YouTube, Hashnode & Notion.
            </p>
          </section>

          {/* ========== PROBLEM SECTION ========== */}
          <section className="mb-16 sm:mb-24">
            <div className="text-center mb-10">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand mb-3">
                Das bekannte Content-Problem
              </h2>
              <p className="text-sm sm:text-base text-[#EFEBDC] max-w-2xl mx-auto">
                Du schreibst einen Artikel. Dann copy-pastest du ihn auf LinkedIn. Dann formatierst du ihn für Medium. Dann kürzt du ihn für Twitter. Dann...
              </p>
            </div>

            {/* Problem Cards */}
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
              {problems.map((problem, index) => (
                <div key={index} ref={problem.ref} className="relative">
                  <div className="bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] p-5 sm:p-6 rounded-lg h-full flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 border border-brand">
                      {problem.icon}
                    </div>
                    <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2">
                      {problem.title}
                    </h3>
                    <p className="text-sm text-slate-700">
                      {problem.description}
                    </p>
                  </div>
                  <RoughBorder
                    parentRef={problem.ref}
                    radius={10}
                    padding={2}
                    color="#AC8E66"
                    strokeWidth={0.3}
                    roughness={1}
                    bowing={2}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* ========== TRENNER ========== */}
          <section className="text-center ">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brand">
              Eine einfache Lösung
            </h2>
          </section>

          {/* ========== FEATURE SECTION ========== */}
          <section className="mb-16 sm:mb-24">
            <div className="text-center mb-10">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-brand mb-3">
                Alles, was man braucht
              </h2>
              <p className="text-sm sm:text-base text-[#EFEBDC]/80 max-w-2xl mx-auto">
                Smarte Funktionen, um deine Content Plattform Produktion schneller und konsistenter zu machen.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {features.map((feature, index) => (
                <div key={index} ref={feature.ref} className="relative">
                  <div className="bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] p-5 sm:p-6 rounded-lg h-full text-center">
                    <div className="w-12 h-12 mx-auto rounded-full border border-brand flex items-center justify-center mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-700">
                      {feature.description}
                    </p>
                  </div>
                  <RoughBorder
                    parentRef={feature.ref}
                    radius={10}
                    padding={2}
                    color="#AC8E66"
                    strokeWidth={0.3}
                    roughness={1}
                    bowing={2}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* ========== CTA BUTTON ========== */}
          <section className="text-center">
            <MonoButton
              onClick={() => {
                trackCTA('Hero_ZenPostStudio');
                navigate('/zenpost-studio');
              }}
              className="text-sm sm:text-base px-8 py-4 border-brand bg-transparent"
              underlineColors="ZenBrand"
              textColor="text-brand"
            >
              ZenPost Studio
            </MonoButton>
          </section>

        </div>
      </PaperGradientBG>
    </>
  );
};

export default ZenPostStudioLandingPageHero;
